import { Colors } from "@/enums/Colors";
import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { DocumentCenterNode, DocumentCenterNodeUtils } from "@/models/DocumentCenterNode";
import { Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import CaretSVG from "../../../assets/images/data-room/caret.svg";
import { LazoCheckbox } from "../common/form/LazoCheckbox";
import { FileIcon } from "../utils/FileIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";

type Props = {
  tree: DocumentCenterNode;
  setTree: (t: DocumentCenterNode) => void;
  level: number;
  matches: Set<DocumentCenterNode>;
};

// Pass virtual root
// example:
//    {
//      name: "root",
//      externalId: "root-node"
//      children: [...] list of real nodes
//    }
function DocumentCenterTree(props: Props) {
  return (
    <>
      {props.tree.children?.map((node) => (
        <DocumentCenterTreeNode
          key={node.externalId}
          node={node}
          level={props.level}
          tree={props.tree}
          setTree={props.setTree}
          matches={props.matches}
        />
      ))}
    </>
  );
}

type TreeNodeProps = {
  node: DocumentCenterNode;
  tree: DocumentCenterNode;
  setTree: (t: DocumentCenterNode) => void;
  level: number;
  matches: Set<DocumentCenterNode>;
};

function DocumentCenterTreeNode(props: TreeNodeProps) {
  const [_selected, setSelected] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(props.node.collapsed || false);

  const m = props.matches?.has(props.node) || props.node.children?.find((n) => props.matches?.has(n)) !== undefined;
  useEffect(() => {
    if (m) {
      setCollapsed(m);
    } else {
      setCollapsed(false);
    }
  }, [props.matches]);

  function onClick() {
    if (props.node.type === GeneralFileNodeType.FOLDER) {
      setCollapsed((prev) => !prev);
    }
  }

  // FORCE UPDATE
  useEffect(() => setSelected(props.node.selected), [props.node.selected]);

  function onCheck() {
    if (props.node.selected) {
      props.setTree(DocumentCenterNodeUtils.treeUpdateNodeState(props.tree, props.node, false));
    } else {
      props.setTree(DocumentCenterNodeUtils.treeUpdateNodeState(props.tree, props.node, true));
    }
  }

  const isIndeterminate = props.node.type === GeneralFileNodeType.FOLDER && DocumentCenterNodeUtils.treeIsIndeterminate(props.node);
  const allChildrenSelected =
    !isIndeterminate && props.node.type === GeneralFileNodeType.FOLDER && DocumentCenterNodeUtils.treeAllSelected(props.node);

  return (
    <>
      <NodeContainer isMatch={m} level={props.level} width="100%">
        <LazoCheckbox
          sx={{
            padding: 0,
            "&&.MuiCheckbox-root": { color: Colors.BLUE_9AA9F9 },
          }}
          onClick={onCheck}
          indeterminate={isIndeterminate}
          checked={props.node.selected || allChildrenSelected || false}
        />
        <img
          src={CaretSVG.src}
          alt={"caret-svg"}
          onClick={onClick}
          style={{
            opacity: props.node.type === GeneralFileNodeType.FOLDER ? 1 : 0,
            transition: "transform 0.1s ease-in",
            transform: collapsed ? "rotate(90deg)" : "",
          }}
        />
        {props.node.type === GeneralFileNodeType.FOLDER ? <FolderNotch onClick={onClick} /> : <FileIcon onClick={onClick} />}
        <Typography onClick={onClick} className="node-name">
          {props.node.name}
        </Typography>
      </NodeContainer>
      {collapsed &&
        props.node.children?.length > 0 &&
        props.node.children.map((c) => (
          <DocumentCenterTreeNode
            key={c.externalId}
            tree={props.tree}
            setTree={props.setTree}
            node={c}
            level={props.level + 1}
            matches={props.matches}
          />
        ))}
    </>
  );
}

const NodeContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "level" && prop !== "isMatch",
})<{ level: number; isMatch: boolean }>(({ level, isMatch }) => ({
  display: "flex",
  alignItems: "center",
  paddingLeft: 5 * level + "px",
  gap: "0.25rem",
  transition: "background-color 0.08s ease-in",
  borderRadius: "0.25rem",
  backgroundColor: isMatch ? Colors.YELLOW_DESATURATED_FFF4BC : "transparent",
  ":hover": {
    cursor: "pointer",
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
  "& .MuiTypography-root": {
    "&.node-name": {
      width: "100%",
      fontSize: "0.875rem",
      textOverflow: "ellipsis",
      textWrap: "nowrap",
      overflow: "hidden",
    },
  },
}));

export default DocumentCenterTree;
